import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import type { ApiResponse } from '~/features/onboarding/types/connection.types';

import type {
  ProductColumn,
  ProductFilterDef,
  ProductFilterOptionsData,
  ProductFilterOptionsParams,
  ProductFilterOptionsRequest,
  ProductFiltersParams,
  ProductFiltersRequest,
} from '../types/product-metadata.types';

/**
 * Fetch the product column catalogue for a connector — one entry per column
 * the backend can return, with label / group / logical type / sortable /
 * filterable. Used by the Manage Columns drawer to render a dynamic list of
 * toggleable fields. Different connectors expose different columns, so the
 * `connectorCode` scopes the schema.
 */
export async function fetchProductColumns(connectorCode: string): Promise<ProductColumn[]> {
  const res = await integrationsClient
    .get(endpoints.products.columns(connectorCode))
    .json<ApiResponse<ProductColumn[]>>();
  // Guarantee the promised array shape: some connectors return a non-array
  // `data` (null / object), which would otherwise crash consumers that call
  // array methods (e.g. `apiColumns.find` in the catalog column factory).
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Fetch the dynamic filter definitions for a connection. The backend narrows
 * option lists based on `appliedFilters`, so posting the current filter state
 * is expected. Keeps the outward API flat — callers pass optional `appliedFilters`.
 */
export async function fetchProductFilters(
  params: ProductFiltersParams,
): Promise<ProductFilterDef[]> {
  const body: ProductFiltersRequest = {
    connectorCode: params.connectorCode,
    connectionId: params.connectionId,
    syncId: params.syncId ?? null,
    ingestionDate: params.ingestionDate ?? null,
    appliedFilters: params.appliedFilters ?? {},
  };
  const res = await integrationsClient
    .post(endpoints.products.filters(), { json: body })
    .json<ApiResponse<ProductFilterDef[]>>();
  return res.data;
}

/**
 * Fetch one page of distinct option values for a single high-cardinality
 * (`paginated`) filter key. Backend supports substring `search` and narrows
 * by `appliedFilters` (self-excluding the queried key). Drives the lazy,
 * searchable, infinite-scroll option list in the filters drawer.
 */
export async function fetchProductFilterOptions(
  params: ProductFilterOptionsParams,
): Promise<ProductFilterOptionsData> {
  const body: ProductFilterOptionsRequest = {
    connectorCode: params.connectorCode,
    connectionId: params.connectionId,
    key: params.key,
    search: params.search ?? null,
    appliedFilters: params.appliedFilters ?? {},
    syncId: params.syncId ?? null,
    ingestionDate: params.ingestionDate ?? null,
    // Defaults match the app's page-size policy (the hook is the sole caller
    // and always passes 100); kept here so a future direct caller stays aligned.
    pagination: { page: params.page ?? 1, size: params.size ?? 100 },
  };
  const res = await integrationsClient
    .post(endpoints.products.filterOptions(), { json: body })
    .json<ApiResponse<ProductFilterOptionsData>>();
  return res.data;
}
